import { ResponseDto } from '@onereach/billing-shared';


/**
 * Fetches paginated data by repeatedly calling the provided function until all pages are retrieved.
 *
 * @template T - The type of the data being fetched.
 * @template Params - The type of additional parameters to be passed to the fetch function.
 * @param {function} fn - A function that takes parameters including the page number and returns a promise of a paginated response.
 * @param {Params} [params] - Optional additional parameters to be included in the request.
 * @returns {Promise<Array<T>>} - A promise that resolves to an array of all fetched data items.
 * @throws {Error} - Throws an error if there is an issue fetching any page of data.
 */
export async function fetchPagedData<T, Params extends object>(
  fn: (params: Params & { page: number; })=> Promise<ResponseDto<T>>,
  params?: Params,
): Promise<Array<T>> {
  let page = 1;
  const results: Array<T> = [];
  const defaultLimit = 500;
  const limit = (params as { limit?: number })?.limit || defaultLimit;

  while (true) {
    const currentParams = {
      ...(params || {}),
      limit,
      page,
    } as Params & { page: number; limit: number };

    try {
      const response = await fn(currentParams);

      if (!response?.data?.length) break;

      results.push(...response.data);
      if (
        response.data.length < limit ||
        (response.total && results.length >= response.total)
      ) break;

      page++;
    } catch (error) {
      throw new Error(`Error fetching page ${page}: ${error}`);
    }
  }

  return results;
}
